import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { differenceInMilliseconds } from "date-fns";
import { EyeIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import { useAppSelector } from "store";

import ChallengeCard from "components/lib/Cards/ChallengeCard";
import { EmptyBox } from "components/lib/UI";

const PublicChallenges = () => {
  const { t } = useTranslation();

  const organisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const user = useAppSelector((state) => state.user);

  const organisationId = organisation?._id;
  const shopWindowQuery = useQuery(
    initUseQuery(api.organisations.getShopWindow, [organisationId ?? ""], {
      isEnabled: !!organisationId,
    }),
  );

  const challenges = useMemo(
    () => shopWindowQuery.data?.challenges ?? [],
    [shopWindowQuery.data?.challenges],
  );

  const openChallenges = useMemo(
    () =>
      challenges
        .filter((challenge) => challenge.stage === "published")
        .sort((a, b) =>
          differenceInMilliseconds(
            new Date(b.createdAt),
            new Date(a.createdAt),
          ),
        ),
    [challenges],
  );
  const closedChallenges = useMemo(
    () =>
      challenges
        .filter((challenge) => challenge.stage === "closed")
        .sort((a, b) =>
          differenceInMilliseconds(
            new Date(b.createdAt),
            new Date(a.createdAt),
          ),
        ),
    [challenges],
  );

  return (
    <div style={{ paddingLeft: 30 }}>
      <H1 className="title" style={{ color: "#3b3b3b" }}>
        Public {t("generic.challenges")}
      </H1>
      {!user && (
        <Link to="/">
          <p style={{ fontSize: 14 }}>
            <u>Log in or sign up to discover more</u>
          </p>
        </Link>
      )}
      {user && (
        <Link to="/challenges/discover">
          <p style={{ fontSize: 14 }}>
            <u>Discover more {t("generic.challenges")}</u>
          </p>
        </Link>
      )}
      <Divider />

      {shopWindowQuery.isLoading ? (
        <Loader style={{ marginTop: 20 }} />
      ) : (
        <div>
          {organisation ? (
            <div>
              {challenges.length >= 1 && (
                <>
                  {openChallenges.length > 0 && (
                    <>
                      <H3>Published {t("generic.challenges")}</H3>
                      <Grid columns={3} tablet={2} mobile={1}>
                        {openChallenges.map((challenge) => (
                          <GridColumn key={challenge._id}>
                            <ChallengeCard challenge={challenge} />
                          </GridColumn>
                        ))}
                      </Grid>
                    </>
                  )}
                  {closedChallenges.length > 0 && (
                    <>
                      <H3>Closed {t("generic.challenges")}</H3>
                      <Grid columns={3} tablet={2} mobile={1}>
                        {closedChallenges.map((challenge) => (
                          <GridColumn key={challenge._id}>
                            <ChallengeCard challenge={challenge} />
                          </GridColumn>
                        ))}
                      </Grid>
                    </>
                  )}
                </>
              )}
              {challenges.length === 0 && (
                <EmptyBox style={{ margin: "20px 40px" }}>
                  <H2>
                    There aren&apos;t any public {t("generic.challenges")} right
                    now
                  </H2>
                  {user ? (
                    <ButtonLink
                      size="large"
                      color="primary"
                      to="/challenges/discover"
                    >
                      <Icon icon={EyeIcon} />
                      {`Discover ${t("generic.challenges")}`}
                    </ButtonLink>
                  ) : (
                    <ButtonLink size="large" color="primary" to="/">
                      {t("entry.login.loginOrSignUp")}
                    </ButtonLink>
                  )}
                </EmptyBox>
              )}
            </div>
          ) : (
            <EmptyBox
              style={{ margin: "20px 40px" }}
              title={`No public ${t("generic.challenges")} available`}
            />
          )}
        </div>
      )}
      <Divider hidden />
    </div>
  );
};

export default PublicChallenges;
